/**
 * User Menu Component
 *
 * Dropdown menu with user avatar and account options.
 * Features:
 * - Header trigger: avatar only (shadcn icon button pattern)
 * - Menu header: name (email available under Profile / Settings)
 * - Account navigation options and sign out
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User, Settings, Link2, CreditCard, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useAvatarSrc } from "@/hooks/useAvatarSrc";

export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("common");

  const handleSignOut = () => {
    navigate("/login", { replace: true });
    logout();
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return "U";
    const first = firstName?.charAt(0) || "";
    const last = lastName?.charAt(0) || "";
    return `${first}${last}`.toUpperCase();
  };

  // Avatars uploaded in Settings are stored as an authenticated path, not a public URL.
  // useAvatarSrc resolves that path to a blob: object URL fetched through the authed client.
  // Render it with a plain <img> + onError fallback rather than Radix <AvatarImage>: that
  // primitive probes the URL with its own new Image() and silently falls back to initials
  // when the probe loses the race against the object-URL lifecycle. ProfilePage's
  // AvatarUpload and UserAvatar already take this plain-<img> approach.
  const avatarSrc = useAvatarSrc(user?.profilePictureUrl);
  const initials = getInitials(user?.firstName, user?.lastName);
  const [imageFailed, setImageFailed] = useState(false);

  // A new source deserves a fresh attempt; without this a single failure sticks for good.
  useEffect(() => {
    setImageFailed(false);
  }, [avatarSrc]);
  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.email ||
    "User";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rounded-full"
          aria-label={`User menu, ${displayName}`}
        >
          <UserMenuAvatar
            src={avatarSrc}
            initials={initials}
            imageFailed={imageFailed}
            onError={() => setImageFailed(true)}
            className="h-8 w-8"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center gap-3">
            <UserMenuAvatar
              src={avatarSrc}
              initials={initials}
              imageFailed={imageFailed}
              onError={() => setImageFailed(true)}
              className="h-9 w-9"
            />
            <p className="text-sm font-medium leading-none truncate">
              {displayName}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => handleNavigate("/settings/profile")}
          className="cursor-pointer"
        >
          <User className="mr-2 h-4 w-4" />
          <span>{t("userMenu.profile")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleNavigate("/settings")}
          className="cursor-pointer"
        >
          <Settings className="mr-2 h-4 w-4" />
          <span>{t("userMenu.settings")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleNavigate("/settings/connected-accounts")}
          className="cursor-pointer"
        >
          <Link2 className="mr-2 h-4 w-4" />
          <span>{t("userMenu.connectedAccounts")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleNavigate("/billing")}
          className="cursor-pointer"
        >
          <CreditCard className="mr-2 h-4 w-4" />
          <span>{t("userMenu.billing")}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleSignOut}
          className="cursor-pointer text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>{t("userMenu.signOut")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Avatar rendered with a plain <img> rather than Radix's <AvatarImage>. The source is a
 * blob: object URL (see useAvatarSrc); Radix pre-loads it with new Image() and silently
 * falls back to initials when that probe doesn't settle cleanly, so a direct <img> with an
 * onError fallback is used instead — matching ProfilePage's AvatarUpload and UserAvatar.
 */
function UserMenuAvatar({
  src,
  initials,
  imageFailed,
  onError,
  className,
}: {
  src?: string;
  initials: string;
  imageFailed: boolean;
  onError: () => void;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-primary-foreground",
        className,
      )}
    >
      {src && !imageFailed ? (
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          onError={onError}
        />
      ) : (
        <span className="text-sm font-medium">{initials}</span>
      )}
    </span>
  );
}
